import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Button,
  Table,
  Spin,
  Typography,
  Tag,
  Space,
  Descriptions,
  message,
  Empty,
  Row,
  Col,
  Tooltip,
  Divider
} from 'antd';
import {
  ReloadOutlined,
  FileTextOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import reportService from '../../services/reportService';
import useAuthStore from '../../store/authStore';

const { Title, Text } = Typography;

const ReportDetailView = ({ visible, report, onClose, onReportUpdated }) => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    if (visible && report) {
      // Auto-generate report when modal opens
      handleGenerateReport();
    }
  }, [visible, report]);

  const handleGenerateReport = async () => {
    if (!report) return;

    try {
      setLoading(true);
      const response = await reportService.generateReport(report._id);
      
      if (response.success) {
        setGeneratedData(response.data.data);
        setMetadata(response.data.metadata);
        message.success('Report generated successfully');
      }
    } catch (error) {
      console.error('Failed to generate report:', error);
      message.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    if (!generatedData || !Array.isArray(generatedData) || generatedData.length === 0) {
      message.warning('No data available to download');
      return;
    }

    try {
      // Convert data to CSV format
      const headers = Object.keys(generatedData[0]).filter(key => key !== '_id' && key !== '__v');
      const csvHeaders = headers.join(',');
      
      const csvRows = generatedData.map(row => {
        return headers.map(header => {
          const value = row[header];
          if (typeof value === 'object' && value !== null) {
            if (value.firstName && value.lastName) {
              return `"${value.firstName} ${value.lastName}"`;
            }
            return `"${JSON.stringify(value)}"`;
          }
          if (typeof value === 'string' && value.includes(',')) {
            return `"${value}"`;
          }
          return value || '';
        }).join(',');
      });

      const csvContent = [csvHeaders, ...csvRows].join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${report?.name || 'report'}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('Report downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      message.error('Failed to download report');
    }
  };

  const renderTable = () => {
    if (!generatedData || !Array.isArray(generatedData)) {
      return (
        <Empty 
          description="No data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // Generate columns based on data structure
    const columns = [];
    if (generatedData.length > 0) {
      const sampleItem = generatedData[0];
      Object.keys(sampleItem).forEach(key => {
        if (key !== '_id' && key !== '__v') {
          columns.push({
            title: key.charAt(0).toUpperCase() + key.slice(1),
            dataIndex: key,
            key,
            render: (value) => {
              if (typeof value === 'object' && value !== null) {
                if (value.firstName && value.lastName) {
                  return `${value.firstName} ${value.lastName}`;
                }
                return JSON.stringify(value);
              }
              if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
              }
              if (key.includes('Date') || key.includes('At')) {
                return value ? new Date(value).toLocaleDateString() : '-';
              }
              return value || '-';
            }
          });
        }
      });
    }

    return (
      <Table
        columns={columns}
        dataSource={generatedData}
        rowKey={(record) => record._id || Math.random()}
        pagination={{
          pageSize: report?.config?.display?.pagination?.pageSize || 20,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
        }}
        scroll={{ x: true }}
      />
    );
  };

  const getReportTypeColor = (type) => {
    const colors = {
      leads: 'blue',
      deals: 'green',
      activities: 'orange',
      performance: 'purple',
      custom: 'default'
    };
    return colors[type] || 'default';
  };

  const getVisibilityColor = (visibility) => {
    const colors = {
      private: 'default',
      team: 'blue',
      tenant: 'green'
    };
    return colors[visibility] || 'default';
  };

  if (!visible || !report) {
    return null;
  }

  return (
    <div className="p-6">
      {/* Header with Back Button */}
      <div className="mb-6">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space>
            <Button 
              icon={<FileTextOutlined />} 
              onClick={onClose}
              type="text"
            >
              ← Back to Reports
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {report?.name}
            </Title>
          </Space>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadReport}
              disabled={!generatedData || !Array.isArray(generatedData) || generatedData.length === 0}
            >
              Download CSV
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => handleGenerateReport()}
              loading={loading}
              type="primary"
            >
              Refresh Data
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={[16, 16]}>
        {/* Report Information */}
        <Col span={24}>
          <Card size="small">
            <Descriptions column={4} size="small">
              <Descriptions.Item label="Type">
                <Tag color={getReportTypeColor(report?.type)}>
                  {report?.type?.charAt(0).toUpperCase() + report?.type?.slice(1)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                <Tag>{report?.category?.charAt(0).toUpperCase() + report?.category?.slice(1)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Visibility">
                <Tag color={getVisibilityColor(report?.visibility)}>
                  {report?.visibility?.charAt(0).toUpperCase() + report?.visibility?.slice(1)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created By">
                {report?.createdBy?.firstName} {report?.createdBy?.lastName}
              </Descriptions.Item>
              <Descriptions.Item label="Last Generated">
                {report?.lastGenerated ? (
                  <Tooltip title={new Date(report.lastGenerated).toLocaleString()}>
                    {formatDistanceToNow(new Date(report.lastGenerated), { addSuffix: true })}
                  </Tooltip>
                ) : (
                  'Never'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Generation Count">
                {report?.generationCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Records">
                {metadata?.recordCount || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Generation Time">
                {metadata?.generationTime ? `${metadata.generationTime}ms` : '-'}
              </Descriptions.Item>
            </Descriptions>
            {report?.description && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary">{report.description}</Text>
              </>
            )}
          </Card>
        </Col>


        {/* Report Data */}
        <Col span={24}>
          <Card>
            <Spin spinning={loading}>
              {renderTable()}
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReportDetailView;
